import classNames from 'classnames';
import { Hint, HintProps } from 'components/hint/Hint';
import { relativePos, translateElement } from 'helpers/positions';
import React, {
  isValidElement,
  PropsWithChildren,
  ReactNode,
  SyntheticEvent,
  useRef,
  useState,
} from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import Icon from 'components/icons/Icon';
import { IconSize, IconSymbol } from 'components/icons/Icon.types';
import { parentClassListContains } from 'helpers/helpers';

import './DropMenu.scss';

type DropMenuTriggerProps = {
  className?: string;
  isTrigger?: boolean;
  hintProps?: HintProps;
};
function DropMenuTrigger(props: DropMenuTriggerProps & PropsWithChildren) {
  const { className, children, hintProps } = props;
  return (
    <>
      <Hint {...hintProps}>
        <div
          className={classNames(
            'dropmenu_trigger',
            {
              ellipsis: !children,
            },
            className,
          )}
        >
          {children || (
            <Icon symbol={IconSymbol.ellipsis} size={IconSize.tiny} />
          )}
        </div>
      </Hint>
    </>
  );
}

function isElementWithClass(
  child: React.ReactNode,
  className: string,
): child is React.ReactElement<{ className: string }> {
  return (
    React.isValidElement(child) &&
    typeof (child.props as any).className === 'string' &&
    // child.props.classList.includes(className)
    (child.props as any).className.split(' ').includes(className)
  );
}

function isMenuItem(child: React.ReactNode): boolean {
  return (
    isValidElement(child) &&
    ((typeof child.type === 'string' &&
      isElementWithClass(child, 'menu_item')) ||
      (typeof child.type === 'function' && (child.type as any).isMenuItem) ||
      (typeof child.type === 'function' &&
        typeof (child.props as { className?: unknown })?.className ===
          'string' &&
        (child.props as { className: string }).className
          .split(' ')
          .includes('menu_item')))
  );
}

function wrapDropMenuItems(
  children: React.ReactNode,
  keyPrefix = '',
  debug = false,
): React.ReactNode {
  if (debug) console.log('children:', React.Children.toArray(children).length);
  return React.Children.map(children, (child, i) => {
    if (debug) console.log('child:', child);
    if (!child) return null;
    if (
      isValidElement(child) &&
      child.type === React.Fragment &&
      (child.props as any).children
    ) {
      // Recursively wrap nested fragment children
      return wrapDropMenuItems(
        (child.props as any).children,
        `${keyPrefix}${i}-`,
      );
    }
    // Do not wrap notes or dividers
    if (
      isValidElement(child) &&
      ((child.type as any).isNote || (child.type as any).isDivider)
    ) {
      return child;
    }
    // Do not wrap if already with class menu_item
    if (isMenuItem(child)) {
      return child;
    }
    // Otherwise, wrap as menu_item
    if (debug) console.log('wrapped child:', child);
    return (
      <div
        className="menu_item"
        data-pd={debug ? 'wrapped' : undefined}
        key={`${keyPrefix}${i}`}
      >
        {child}
      </div>
    );
  });
}

type DropMenuItemProps = {
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
};
function DropMenuItem(props: PropsWithChildren & DropMenuItemProps) {
  return (
    <div className="menu_item" onClick={props.onClick}>
      {props.children}
    </div>
  );
}

type DropMenuItemsProps = {
  className?: string;
  disabled?: boolean;
  debug?: boolean;
};
function DropMenuItems({
  className,
  children,
  disabled = false,
  debug,
}: DropMenuItemsProps & PropsWithChildren) {
  return React.Children.count(children) ? (
    <div className={classNames('dropmenu_items', className, { disabled })}>
      {wrapDropMenuItems(children, undefined, debug)}
    </div>
  ) : null;
}
DropMenuItems.isItems = true;

type DropMenuNoteProps = {
  className?: string;
};
function DropMenuNote({
  className,
  children,
}: DropMenuNoteProps & PropsWithChildren) {
  return (
    <div className={classNames('dropmenu_note label', className)}>
      {children}
    </div>
  );
}
DropMenuNote.isNote = true;

function DropMenuDivider({ className }: DropMenuNoteProps) {
  return <div className={classNames('dropmenu_divider', className)} />;
}
DropMenuDivider.isNote = true;

type DropMenuProps = {
  className?: string;
  noPosition?: boolean;
  right?: boolean;
  shift?: number; // translate horizontally in px
  lift?: number; // translate vertically in px
  up?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>; // the ancestor relative div in which the menu is rendered
  containerTailHeight?: number; // extra space in px to observe in the tail of the container
  // called before showing the menu, return false to prevent
  onOpen?: (e: SyntheticEvent) => boolean;
  // boolean or function that determines if the menu should be closed on click
  shouldClose?: boolean | ((e: SyntheticEvent) => boolean); // set this to true to close the menu
  hideWithOpacity?: boolean;
  forceTrigger?: boolean;
  debug?: boolean;
  children: ReactNode;
};

export default function DropMenu(props: DropMenuProps): React.JSX.Element {
  const {
    className,
    noPosition = false,
    right = false,
    shift,
    lift,
    up = false,
    containerRef,
    containerTailHeight = 0,
    onOpen,
    shouldClose = true,
    forceTrigger,
    debug,
    children,
  } = props;
  const [isActive, setIsActive] = useState(false);
  const dropMenuRef = useRef<HTMLDivElement>(null!);

  function onClickOutside(event: MouseEvent | TouchEvent | FocusEvent) {
    if (isActive) {
      const target = event.target as HTMLElement;
      // Check if any ancestor has class 'popupmenu'
      let el: HTMLElement | null = target;
      while (el) {
        if (el.classList && el.classList.contains('popmenu')) {
          return; // Don't close if inside a popmenu
        }
        el = el.parentElement;
      }
      // const items = dropMenuRef.current!.querySelector(
      //   '.dropmenu_items',
      // ) as HTMLElement;
      // transform(items);
      setIsActive(false);
    }
  }
  useOnClickOutside(dropMenuRef, onClickOutside);

  function onClick(e: React.MouseEvent<HTMLDivElement>) {
    // if (!parentClassListContains(e.target as HTMLElement, 'dropmenu_trigger'))
    //   return;
    e.stopPropagation();
    if (containerRef && containerRef.current) {
      const relpos = relativePos(dropMenuRef.current, containerRef.current);
      const menurect = dropMenuRef.current!.getBoundingClientRect();
      const items = dropMenuRef.current!.querySelector(
        '.dropmenu_items',
      ) as HTMLElement;
      if (!isActive && relpos?.invy) {
        items.style.height = 'auto'; // set height so we can calculate
        const itemsrect = items!.getBoundingClientRect();
        const mh = itemsrect.height;
        const my = itemsrect.top - menurect.top + 15;
        items.style.height = ''; // remove height after calculation
        const space = relpos.invy - mh - my - containerTailHeight;
        if (space < 0) {
          translateElement(items, { x: shift ?? 0, y: space + (lift ?? 0) });
        } else translateElement(items, { x: shift ?? 0, y: lift ?? 0 });
      } else translateElement(items, { x: shift ?? 0, y: lift ?? 0 });
    } else {
      const items = dropMenuRef.current!.querySelector(
        '.dropmenu_items',
      ) as HTMLElement;
      translateElement(items, { x: shift ?? 0, y: lift ?? 0 });
    }
    if (isActive) {
      if (parentClassListContains(e.target as HTMLElement, 'dropmenu_trigger'))
        setIsActive(false);
      else if (typeof shouldClose === 'function' ? shouldClose(e) : shouldClose)
        setIsActive(false);
    } else if (!onOpen || onOpen(e)) setIsActive(true);
  }

  function dropmenuClassNames() {
    return classNames('dropmenu', className, {
      dropmenu_noposition: noPosition,
      dropmenu_active: isActive,
      dropmenu_right: right,
      dropmenu_up: up,
    });
  }

  function hasItems(): boolean {
    for (const child of React.Children.toArray(children)) {
      if (React.isValidElement(child)) {
        if ((child.type as any).isItems) {
          return React.Children.toArray((child.props as any).children).some(
            (c) => !!c,
          );
        }
      }
    }
    return false;
  }

  return forceTrigger || hasItems() ? (
    <div className={dropmenuClassNames()} ref={dropMenuRef} onClick={onClick}>
      {children}
    </div>
  ) : (
    <></>
  );
}

DropMenu.Trigger = DropMenuTrigger;
DropMenu.Items = DropMenuItems;
DropMenu.Item = DropMenuItem;
DropMenu.Note = DropMenuNote;
DropMenu.Divider = DropMenuDivider;
